import type { ReactNode } from 'react';
import { Bookmark, Package, ScrollText, SquareKanban, Tags, Truck, type LucideIcon } from 'lucide-react';
import { POLICY_STATUS_LABELS, type PolicySearchHit } from '@shared/types/policies';
import type { TaskSearchHit } from '@shared/types/tasks';
import { formatTaskNumber } from '@shared/task-number';
import {
  buildSavedFilterHref,
  SavedFilterNormalizationError,
} from '@shared/saved-filter-query';
import {
  searchPolicies,
  searchProducts,
  searchSavedFilters,
  searchSuppliers,
  searchTasks,
  searchVendors,
  type ProductSearchItem,
  type SavedFilterSearchItem,
  type SupplierSearchItem,
  type VendorSearchItem,
} from '@/lib/api/omni-search';
import { EMPTY, formatCurrency, formatNumber } from '@/lib/formatters';
import { savedFilterPageLabel } from './saved-filter-page-label';
import type { OmniEntityKind } from './types';

export type EntitySearchSource = {
  kind: OmniEntityKind;
  heading: string;
  icon: LucideIcon;
  listPath: string;
  search: (q: string, limit: number) => Promise<unknown[]>;
  getId: (row: unknown) => string;
  getLabel: (row: unknown) => string;
  href: (row: unknown) => string;
  renderOption: (row: unknown) => ReactNode;
};

function productName(r: ProductSearchItem): string {
  return r.product_title || r.displayname || 'Untitled product';
}

function variantDetail(r: ProductSearchItem): string | null {
  const title = r.variant_title?.trim();
  if (!title || title.toLowerCase() === 'default title') return null;
  if (r.product_title && title === r.product_title) return null;
  if (r.displayname && title === r.displayname) return null;
  return title;
}

function stockLabel(qty: string | number | null): string {
  if (qty == null || qty === '') return EMPTY;
  const n = typeof qty === 'number' ? qty : Number(qty);
  if (Number.isNaN(n)) return String(qty);
  return formatNumber(n, { maximumFractionDigits: 0 });
}

function priceLabel(r: ProductSearchItem): string {
  const raw = r.unitprice ?? r.price;
  if (raw == null || raw === '') return EMPTY;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (Number.isNaN(n)) return EMPTY;
  return formatCurrency(n);
}

function ProductOption({ r }: { r: ProductSearchItem }) {
  const detail = variantDetail(r);
  const meta = [
    r.code ? `SKU ${r.code}` : null,
    detail,
    r.vendor_name,
  ].filter(Boolean);

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{productName(r)}</div>
        <div className="truncate text-xs text-muted-foreground">
          {meta.length > 0 ? meta.join(' · ') : EMPTY}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs tabular-nums">
        <div className="font-medium">{priceLabel(r)}</div>
        <div className="text-muted-foreground">
          {stockLabel(r.quantityonhand)} OH
        </div>
      </div>
    </div>
  );
}

function asProduct(row: unknown): ProductSearchItem {
  return row as ProductSearchItem;
}
function asVendor(row: unknown): VendorSearchItem {
  return row as VendorSearchItem;
}
function asSupplier(row: unknown): SupplierSearchItem {
  return row as SupplierSearchItem;
}
function asTask(row: unknown): TaskSearchHit {
  return row as TaskSearchHit;
}
function asPolicy(row: unknown): PolicySearchHit {
  return row as PolicySearchHit;
}
function asSavedFilter(row: unknown): SavedFilterSearchItem {
  return row as SavedFilterSearchItem;
}

function savedFilterHref(row: SavedFilterSearchItem): string {
  try {
    return buildSavedFilterHref(row.pathname, row.queryString);
  } catch (err) {
    if (err instanceof SavedFilterNormalizationError) {
      return row.pathname;
    }
    throw err;
  }
}

export const ENTITY_REGISTRY: Record<OmniEntityKind, EntitySearchSource> = {
  products: {
    kind: 'products',
    heading: 'Products',
    icon: Package,
    listPath: '/products',
    search: (q, limit) => searchProducts(q, limit),
    getId: (row) => asProduct(row).variant_id,
    getLabel: (row) => productName(asProduct(row)),
    href: (row) => `/variant-details/${asProduct(row).variant_id}`,
    renderOption: (row) => <ProductOption r={asProduct(row)} />,
  },
  tasks: {
    kind: 'tasks',
    heading: 'Tasks',
    icon: SquareKanban,
    listPath: '/tasks',
    search: (q, limit) => searchTasks(q, limit),
    getId: (row) => asTask(row).id,
    getLabel: (row) => asTask(row).title,
    href: (row) => `/tasks/${asTask(row).id}`,
    renderOption: (row) => {
      const t = asTask(row);
      const status = t.archived ? 'Archived' : t.column_name;
      return (
        <div className={t.archived || t.is_done ? 'min-w-0 opacity-70' : 'min-w-0'}>
          <div className="truncate font-medium">{t.title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {[formatTaskNumber(t.number), status, t.context_title].filter(Boolean).join(' · ')}
          </div>
        </div>
      );
    },
  },
  policies: {
    kind: 'policies',
    heading: 'Policies',
    icon: ScrollText,
    listPath: '/policies',
    search: (q, limit) => searchPolicies(q, limit),
    getId: (row) => asPolicy(row).id,
    getLabel: (row) => asPolicy(row).title,
    href: (row) => `/policies/${asPolicy(row).id}`,
    renderOption: (row) => {
      const p = asPolicy(row);
      const meta = [p.section_name, p.kind === 'procedure' ? 'Procedure' : null, p.archived ? 'Archived' : POLICY_STATUS_LABELS[p.status]];
      return (
        <div className={p.archived ? 'min-w-0 opacity-70' : 'min-w-0'}>
          <div className="truncate font-medium">{p.title}</div>
          <div className="truncate text-xs text-muted-foreground">{meta.filter(Boolean).join(' · ')}</div>
        </div>
      );
    },
  },
  vendors: {
    kind: 'vendors',
    heading: 'Brands',
    icon: Tags,
    listPath: '/vendors',
    search: (q, limit) => searchVendors(q, limit),
    getId: (row) => asVendor(row).id,
    getLabel: (row) => asVendor(row).name,
    href: (row) => `/vendors/${asVendor(row).id}`,
    renderOption: (row) => {
      const v = asVendor(row);
      const count = v.product_count ?? 0;
      return (
        <div className="min-w-0">
          <div className="truncate font-medium">{v.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {count} product{count === 1 ? '' : 's'}
          </div>
        </div>
      );
    },
  },
  suppliers: {
    kind: 'suppliers',
    heading: 'Suppliers',
    icon: Truck,
    listPath: '/suppliers',
    search: (q, limit) => searchSuppliers(q, limit),
    getId: (row) => asSupplier(row).id,
    getLabel: (row) => asSupplier(row).name,
    href: (row) => `/suppliers/${asSupplier(row).id}`,
    renderOption: (row) => {
      const s = asSupplier(row);
      const meta = [
        s.email,
        typeof s.vendor_count === 'number'
          ? `${s.vendor_count} brand${s.vendor_count === 1 ? '' : 's'}`
          : null,
      ].filter(Boolean);
      return (
        <div className="min-w-0">
          <div className="truncate font-medium">{s.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {meta.length > 0 ? meta.join(' · ') : EMPTY}
          </div>
        </div>
      );
    },
  },
  saved_filters: {
    kind: 'saved_filters',
    heading: 'Saved views',
    icon: Bookmark,
    listPath: '/reports',
    search: (q, limit) => searchSavedFilters(q, limit),
    getId: (row) => asSavedFilter(row).id,
    getLabel: (row) => {
      const f = asSavedFilter(row);
      return `${f.name} · ${savedFilterPageLabel(f.pathname)}`;
    },
    href: (row) => savedFilterHref(asSavedFilter(row)),
    renderOption: (row) => {
      const f = asSavedFilter(row);
      const page = savedFilterPageLabel(f.pathname);
      const hasFilters = Boolean(f.queryString?.trim());
      return (
        <div className="min-w-0">
          <div className="truncate font-medium">{f.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {page}
            {hasFilters ? ' · with filters' : ''}
          </div>
        </div>
      );
    },
  },
};

export const DEFAULT_ENTITY_KINDS: OmniEntityKind[] = [
  'products',
  'tasks',
  'policies',
  'vendors',
  'suppliers',
  'saved_filters',
];
